'use client'

import React, { useState } from 'react'
import type { ContactBlock } from '@/payload-types'
import { cn } from '@/utilities/cn'
import { Button } from '@/components/ui/button'
import { Phone } from 'lucide-react'
import Link from 'next/link'

type Props = {
  className?: string
} & ContactBlock

export const ContactBlockComponent: React.FC<Props> = ({
  heading,
  description,
  phoneSection,
  secondarySection,
  buttonText,
  buttonLink,
  className,
}) => {
  const [formData, setFormData] = useState({
    fullName: '',
    phoneNumber: '',
    address: '',
    email: '',
    requestedService: '',
    dayOfService: '',
    note: '',
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitMessage, setSubmitMessage] = useState('')

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitMessage('')

    try {
      // Here you would typically send the form data to your API
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      if (response.ok) {
        setSubmitMessage('Tack för ditt meddelande. Vi återkommer till dig snart!')
        setFormData({
          fullName: '',
          phoneNumber: '',
          address: '',
          email: '',
          requestedService: '',
          dayOfService: '',
          note: '',
        })
      } else {
        setSubmitMessage('Något gick fel. Vänligen försök igen.')
      }
    } catch (_error) {
      setSubmitMessage('Något gick fel. Vänligen försök igen.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section className={cn('py-20 lg:py-32 bg-gray-50', className)}>
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-12 items-start max-w-7xl mx-auto">
          {/* Left Content */}
          <div className="max-w-xl">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#211F54] leading-tight mb-6">
              {heading}
            </h2>
            
            {description && (
              <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                {description}
              </p>
            )}
            
            {/* Phone Section */}
            {phoneSection && (
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-[#0066CC] p-3 rounded-full">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 uppercase tracking-wider">
                      {phoneSection.label}
                    </p>
                    <p className="text-2xl font-bold text-[#211F54]">
                      {phoneSection.number}
                    </p>
                  </div>
                </div>
                <div className="w-full h-px bg-gray-200"></div>
              </div>
            )}
            
            {/* Secondary Section */}
            {secondarySection && (
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-[#211F54] mb-4">
                  {secondarySection.heading}
                </h3>
                <p className="text-gray-600 mb-6">
                  {secondarySection.text}
                </p>
                
                {buttonText && (
                  <Button 
                    variant="ghost" 
                    className="text-[#0066CC] hover:text-[#211F54] p-0 h-auto font-medium"
                    asChild
                  >
                    <Link href={buttonLink || '#'}>
                      {buttonText} →
                    </Link>
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Right Contact Form */}
          <div className="bg-white rounded-3xl p-8 lg:p-10 shadow-lg">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Row 1: Full name and Phone number */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
                    Fullständigt namn
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-[#0066CC] focus:border-transparent focus:bg-white transition-all"
                    placeholder="Anna Andersson"
                  />
                </div>
                
                <div>
                  <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-2">
                    Telefonnummer
                  </label>
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-[#0066CC] focus:border-transparent focus:bg-white transition-all"
                    placeholder="070-123 45 67"
                  />
                </div>
              </div>

              {/* Row 2: Address and Email */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                    Adress
                  </label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-[#0066CC] focus:border-transparent focus:bg-white transition-all"
                    placeholder="Storgatan 12, Stockholm"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    E-post
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-[#0066CC] focus:border-transparent focus:bg-white transition-all"
                    placeholder="anna@exempel.se"
                  />
                </div>
              </div>

              {/* Row 3: Requested service and Day of service */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="requestedService" className="block text-sm font-medium text-gray-700 mb-2">
                    Önskad tjänst
                  </label>
                  <input
                    type="text"
                    id="requestedService"
                    name="requestedService"
                    value={formData.requestedService}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-[#0066CC] focus:border-transparent focus:bg-white transition-all"
                    placeholder="Helstädning"
                  />
                </div>
                
                <div>
                  <label htmlFor="dayOfService" className="block text-sm font-medium text-gray-700 mb-2">
                    Datum för tjänst
                  </label>
                  <input
                    type="date"
                    id="dayOfService"
                    name="dayOfService"
                    value={formData.dayOfService}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-[#0066CC] focus:border-transparent focus:bg-white transition-all"
                  />
                </div>
              </div>

              {/* Row 4: Add a note */}
              <div>
                <label htmlFor="note" className="block text-sm font-medium text-gray-700 mb-2">
                  Lägg till en kommentar
                </label>
                <textarea
                  id="note"
                  name="note"
                  value={formData.note}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-[#0075FF] focus:border-transparent focus:bg-white transition-all resize-none"
                  placeholder="Skriv ditt meddelande här..."
                />
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#0066CC] hover:bg-[#211F54] text-white py-4 text-lg rounded-xl font-medium"
              >
                {isSubmitting ? 'Skickar...' : 'Skicka meddelande'}
              </Button>

              {/* Submit Message */}
              {submitMessage && (
                <p className={cn(
                  "text-center",
                  submitMessage.includes('Tack') ? 'text-green-600' : 'text-red-600'
                )}>
                  {submitMessage}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  )
}